import { useCallback, useEffect, useRef, useState } from "react";

import { obtenerProyecto } from "../api/client";
import type { Proyecto } from "../api/types";

// Sondeo (polling) de GET /projects/{id}: mientras `estado === "generando"`
// el proyecto vuelve a pedirse cada INTERVALO_SONDEO_MS. La respuesta ya trae
// `progress` (etapa/pct/mensaje) y `png_url`, asi que un solo endpoint
// alcanza para pintar la barra de progreso y el visor del PNG (no hace falta
// GET /generation aparte).
//
// El efecto de sondeo se reprograma leyendo `proyecto` en sus dependencias:
// cada vez que `cargar()` trae un proyecto nuevo con estado "generando", el
// efecto se re-ejecuta y agenda el siguiente tick; en cuanto el estado deja
// de ser "generando" (listo/fallido/cuota_agotada), no se agenda mas y el
// sondeo se detiene solo.
const INTERVALO_SONDEO_MS = 2000;

export interface EstadoProyectoHook {
  proyecto: Proyecto | null;
  cargando: boolean;
  error: string | null;
  refrescar: () => Promise<void>;
}

export function useProyecto(
  token: string,
  proyectoId: string | null,
): EstadoProyectoHook {
  const [proyecto, setProyecto] = useState<Proyecto | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const activoRef = useRef(true);

  const cargar = useCallback(async (): Promise<void> => {
    if (!proyectoId) return;
    try {
      const datos = await obtenerProyecto(token, proyectoId);
      if (!activoRef.current) return;
      setProyecto(datos);
      setError(null);
    } catch (err) {
      if (!activoRef.current) return;
      setError(err instanceof Error ? err.message : "Error desconocido");
    } finally {
      if (activoRef.current) setCargando(false);
    }
  }, [token, proyectoId]);

  // Reset + primera carga al cambiar de proyecto.
  useEffect(() => {
    activoRef.current = true;
    setProyecto(null);
    setError(null);
    if (proyectoId) {
      setCargando(true);
      void cargar();
    }
    return () => {
      activoRef.current = false;
    };
  }, [proyectoId, cargar]);

  // Bucle de sondeo (ver comentario del modulo).
  useEffect(() => {
    if (!proyectoId || proyecto?.estado !== "generando") return;
    const temporizador = window.setTimeout(() => {
      void cargar();
    }, INTERVALO_SONDEO_MS);
    return () => window.clearTimeout(temporizador);
  }, [proyectoId, proyecto, cargar]);

  return { proyecto, cargando, error, refrescar: cargar };
}
